import React from 'react';
import { Link } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import {
  ShoppingBag,
  Package,
  BookOpen,
  Box,
  Shield,
  ExternalLink,
  Info,
  ArrowLeft,
  Loader2,
  ShoppingCart,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Block } from '../components/ui/Block';
import {
  usePartnerProducts,
  type PartnerCategory,
  type PartnerProduct,
} from '../hooks/usePartnerProducts';

interface Props {
  user?: User | null;
  publicMode?: boolean;
  embedded?: boolean;
}

const CATEGORY_TITLES: Record<PartnerCategory, string> = {
  figurinhas: 'Figurinhas',
  albuns: 'Álbuns',
  acessorios: 'Acessórios',
};

const CATEGORY_DESCRIPTIONS: Partial<Record<PartnerCategory, string>> = {
  figurinhas: 'Escolha o bundle que cabe no seu bolso.',
};

const ICON_MAP: Record<string, LucideIcon> = {
  Package,
  BookOpen,
  Box,
  Shield,
  ShoppingBag,
  ShoppingCart,
};

function resolveIcon(name: string | null): LucideIcon {
  if (name && ICON_MAP[name]) return ICON_MAP[name];
  return ShoppingBag;
}

const openProduct = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

function ProductCard({ product }: { product: PartnerProduct }) {
  const Icon = resolveIcon(product.icon);
  return (
    <article className="bg-white border border-ink-300 rounded-xl p-3.5 mb-3 last:mb-0 flex items-start gap-3">
      {product.image_url ? (
        <div className="w-16 h-16 rounded-md bg-ink-100 overflow-hidden shrink-0 border border-ink-300">
          <img
            src={product.image_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <span className="w-11 h-11 rounded-md bg-green-100 inline-flex items-center justify-center shrink-0 text-green-700">
          <Icon size={22} strokeWidth={2.25} />
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <h3 className="font-display font-bold text-[14px] text-ink-900 leading-tight">
            {product.title}
          </h3>
          {product.badge && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-gold-500 text-ink-900 text-[10px] font-display font-bold tracking-wide">
              {product.badge}
            </span>
          )}
        </div>
        {product.subtitle && (
          <p className="text-caption text-ink-500 mb-2 leading-snug">{product.subtitle}</p>
        )}
        <button
          type="button"
          onClick={() => openProduct(product.affiliate_url)}
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-green-700 text-white font-display font-bold text-[12px] hover:bg-green-900 btn-focus"
        >
          Ver no Mercado Livre
          <ExternalLink size={12} strokeWidth={2.5} />
        </button>
      </div>
    </article>
  );
}

function PageBody() {
  const { grouped, loading, error } = usePartnerProducts();

  return (
    <div className="pb-4">
      <header className="mb-5 px-1">
        <p className="bt-eyebrow text-green-700 mb-2">Parceiros</p>
        <h1 className="font-display font-bold text-h1 text-ink-900 tracking-[-0.015em]">
          Produtos selecionados pra completar seu álbum.
        </h1>
      </header>

      <div className="rounded-xl border border-gold-500 bg-gold-100 px-3.5 py-3 mb-5 flex gap-2.5">
        <Info size={18} strokeWidth={2.25} className="text-gold-600 shrink-0 mt-0.5" />
        <p className="text-small text-ink-900 leading-snug">
          Esta área contém links de parceiros afiliados. Quando você compra por aqui,
          recebemos uma comissão, sem custo extra para você. Isso nos ajuda a manter o app gratuito.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-ink-500 gap-3">
          <Loader2 size={18} className="animate-spin text-green-700" />
          <span className="font-body text-small">Carregando produtos...</span>
        </div>
      ) : error ? (
        <div className="rounded-xl border border-ruby-500 bg-ruby-100 px-3.5 py-3 text-small text-ruby-700">
          Não foi possível carregar os produtos. Tente novamente em instantes.
        </div>
      ) : grouped.length === 0 ? (
        <div className="bg-white border border-dashed border-ink-300 rounded-xl py-12 px-6 text-center">
          <ShoppingBag size={40} className="mx-auto text-ink-300 mb-4" strokeWidth={1.75} />
          <p className="font-display font-semibold text-h3 text-ink-700">Nenhum produto agora</p>
          <p className="font-body text-small text-ink-500 mt-1">
            Volte depois para conferir as novidades.
          </p>
        </div>
      ) : (
        grouped.map((group) => (
          <Block
            key={group.category}
            title={CATEGORY_TITLES[group.category]}
            defaultOpen
            description={CATEGORY_DESCRIPTIONS[group.category]}
          >
            {group.products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </Block>
        ))
      )}
    </div>
  );
}

export default function ParceirosPage({ embedded, publicMode }: Props) {
  if (embedded) {
    return <PageBody />;
  }

  return (
    <div className="min-h-screen bg-paper">
      <header className="bg-green-700 text-white sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-shell mx-auto px-4 md:px-6 py-3.5 flex items-center gap-3">
          <Link
            to={publicMode ? '/' : '/album'}
            aria-label="Voltar"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border-[1.5px] border-white/40 text-white hover:bg-white/10"
          >
            <ArrowLeft size={16} strokeWidth={2.25} />
          </Link>
          <ShoppingBag size={18} strokeWidth={2.25} />
          <h1 className="text-base font-display font-semibold tracking-[0.02em]">Parceiros</h1>
        </div>
      </header>
      <main className="max-w-shell mx-auto px-4 md:px-6 pt-5 pb-12">
        <PageBody />
      </main>
    </div>
  );
}
